import { forwardRef, useRef, useState } from 'libs/react';
import { React } from 'libs/react';
import './TextInput.scss';
/**
 * @component TextInput
 * @since 1.0.0
 * @hidden
 */
export const TextInput = forwardRef((props, ref) => {
    let { type, name, ident, value, defaultValue, scale, required, disabled, step, min, max, placeholder, autoselect, autocorrect, autocomplete, autocapitalize, pattern, className } = props;
    const internalRef = useRef(null);
    const [showPassword, setShowPassword] = useState(false);
    /**
     * @function onBlur
     * @since 1.0.0
     * @hidden
     */
    function onBlur() {
        if (props.onBlur) {
            props.onBlur();
        }
    }
    /**
     * @function onFocus
     * @since 1.0.0
     * @hidden
     */
    function onFocus() {
        if (autoselect && (internalRef === null || internalRef === void 0 ? void 0 : internalRef.current)) {
            internalRef.current.select();
        }
        if (props.onFocus) {
            props.onFocus();
        }
    }
    const onRef = (node) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        internalRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        }
        else if (ref) {
            ref.current = node;
        }
    };
    /**
     * @function onChange
     * @since 1.0.0
     * @hidden
     */
    function onChange(e) {
        if (props.onChange) {
            const inputValue = e.currentTarget.value;
            const storableValue = getStorableValue(inputValue, type, scale);
            // Valider min/max pour les inputs de type number
            if (type === 'number' && storableValue !== '') {
                const numericValue = Number(storableValue);
                // Permettre les valeurs vides pendant la saisie, mais valider les valeurs numériques
                if (!isNaN(numericValue)) {
                    if (min !== undefined && numericValue < Number(min)) {
                        return; // Ne pas propager la valeur si elle est inférieure au minimum
                    }
                    if (max !== undefined && numericValue > Number(max)) {
                        return; // Ne pas propager la valeur si elle est supérieure au maximum
                    }
                }
            }
            props.onChange(storableValue, e);
        }
    }
    /**
     * @function onKeyDown
     * @since 1.0.0
     * @hidden
     */
    function onKeyDown(e) {
        // Empêcher le signe moins si le minimum est supérieur à 0
        if (type === 'number' &&
            min &&
            Number(min) > 0 &&
            (e.key == 'Minus' || e.key == '-')) {
            e.preventDefault();
            return;
        }
        if (props.onKeyDown) {
            props.onKeyDown(e);
        }
    }
    /**
     * @function togglePasswordVisibility
     * @since 1.0.0
     * @hidden
     */
    function togglePasswordVisibility() {
        setShowPassword(!showPassword);
    }
    if (value) {
        value = getFormatedValue(value, type, scale);
    }
    const isPasswordType = type === 'password';
    const inputType = isPasswordType && showPassword ? 'text' : type;
    return (React.createElement("div", { className: `text-input-wrapper ${isPasswordType ? 'has-password-toggle' : ''}` },
        React.createElement("input", { id: ident, ref: onRef, className: className, name: name, type: inputType, value: value, defaultValue: defaultValue, required: required, disabled: disabled, placeholder: placeholder, step: step, min: min, max: max, autoCorrect: autocorrect ? 'on' : 'off', autoComplete: autocomplete ? 'on' : 'off', autoCapitalize: autocapitalize ? 'on' : 'off', onBlur: onBlur, onFocus: onFocus, onInput: onChange, onChange: onChange, onKeyDown: onKeyDown, pattern: pattern }),
        isPasswordType && (React.createElement("button", { type: "button", className: "password-toggle-btn", onClick: togglePasswordVisibility, disabled: disabled, "aria-label": showPassword
                ? 'Masquer le mot de passe'
                : 'Afficher le mot de passe' }, showPassword ? (React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "currentColor" },
            React.createElement("path", { d: "M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" }))) : (React.createElement("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "currentColor" },
            React.createElement("path", { d: "M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" })))))));
});
/**
 * @function getFormatedValue
 * @since 1.0.0
 * @hidden
 */
export function getFormatedValue(value, type, scale) {
    if (type == 'number') {
        if (scale) {
            value = String(parseFloat(sanitize(value)) * scale);
        }
    }
    return value;
}
/**
 * @function getStorableValue
 * @since 1.0.0
 * @hidden
 */
export function getStorableValue(value, type, scale) {
    if (type == 'number') {
        if (scale) {
            value = String(parseFloat(sanitize(value)) / scale);
        }
    }
    return value;
}
/**
 * @function sanitize
 * @since 1.0.0
 * @hidden
 */
export function sanitize(value) {
    value = value.replace(/,+/g, '.');
    value = value.replace(/[^0-9.]/g, '');
    return value;
}
